import React, { useEffect, useMemo, useState } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import ValidatedEmailInput from '../components/ValidatedEmailInput';
import { listSystemRoles } from '../services/systemRoleService';
import {
  inviteSystemUser,
  saveSystemUser,
  type BackgroundInviteResult,
} from '../services/systemUserService';

interface SystemRoleOption {
  _id: string;
  name: string;
  status?: string;
}

interface SystemUserRecord {
  _id?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  title?: string;
  position?: string;
  status?: string;
  systemRole?: { roleId: string; roleName: string } | null;
  systemRoleId?: string;
}

interface EditSystemUserModalProps {
  user?: SystemUserRecord | null;
  onClose: () => void;
  onSaved: (user: any, meta?: { invited: boolean }) => void;
  onBackgroundInviteResult?: (result: BackgroundInviteResult) => void;
}

const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500';
const selectCls = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 bg-white';

export default function EditSystemUserModal({ user, onClose, onSaved, onBackgroundInviteResult }: EditSystemUserModalProps) {
  const [roles, setRoles] = useState<SystemRoleOption[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    email: user?.email || '',
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    title: user?.title || '',
    position: user?.position || '',
    status: user?.status || 'active',
    systemRoleId: user?.systemRole?.roleId || user?.systemRoleId || '',
  });

  useEffect(() => {
    let active = true;
    listSystemRoles()
      .then((items) => {
        if (!active) return;
        setRoles((items as SystemRoleOption[]).filter((role) => role.status !== 'inactive'));
      })
      .catch(() => {
        if (!active) return;
        setRoles([]);
      });
    return () => {
      active = false;
    };
  }, []);

  const selectedRole = useMemo(
    () => roles.find((role) => role._id === form.systemRoleId) || null,
    [form.systemRoleId, roles],
  );

  const handleChange = (key: keyof typeof form, value: string) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const handleSave = async () => {
    const email = form.email.trim();
    const firstName = form.firstName.trim();
    const lastName = form.lastName.trim();

    if (!email) {
      setError('Email is required.');
      return;
    }
    if (!firstName || !lastName) {
      setError('First name and last name are required.');
      return;
    }
    if (!form.systemRoleId || !selectedRole) {
      setError('A system role is required.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      if (!user?._id) {
        // New user: local-first create + background invitation (mirrors
        // tenant-web). The user lands in the grid immediately from Dexie.
        const created = await inviteSystemUser({
          email,
          firstName,
          lastName,
          title: form.title.trim(),
          position: form.position.trim(),
          roleId: selectedRole._id,
          roleName: selectedRole.name,
          onBackgroundInviteResult,
        });
        onSaved(created, { invited: true });
        return;
      }

      const saved = await saveSystemUser({
        ...user,
        email,
        firstName,
        lastName,
        title: form.title.trim(),
        position: form.position.trim(),
        status: form.status,
        systemRole: {
          roleId: selectedRole._id,
          roleName: selectedRole.name,
        },
      });
      onSaved(saved, { invited: false });
    } catch (err: any) {
      setError(err?.message || 'Failed to save system user.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50 shrink-0">
          <div>
            <h3 className="text-base font-semibold text-gray-900">{user?._id ? 'Edit System User' : 'New System User'}</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {user?._id ? 'Update system account details and platform role' : 'Create a platform-only system user account and send their invitation'}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="flex items-center gap-2 px-4 py-2.5 mb-4 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Email Address</label>
              <ValidatedEmailInput
                value={form.email}
                onChange={(value) => handleChange('email', value)}
                className={inputCls + ' pr-9'}
                placeholder="name@company.com"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">First Name</label>
              <input
                type="text"
                value={form.firstName}
                onChange={(e) => handleChange('firstName', e.target.value)}
                className={inputCls}
                placeholder="First name"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Last Name</label>
              <input
                type="text"
                value={form.lastName}
                onChange={(e) => handleChange('lastName', e.target.value)}
                className={inputCls}
                placeholder="Last name"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Title</label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => handleChange('title', e.target.value)}
                className={inputCls}
                placeholder="Optional title"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Position</label>
              <input
                type="text"
                value={form.position}
                onChange={(e) => handleChange('position', e.target.value)}
                className={inputCls}
                placeholder="Optional position"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">System Role</label>
              <select
                value={form.systemRoleId}
                onChange={(e) => handleChange('systemRoleId', e.target.value)}
                className={selectCls}
              >
                <option value="">Select system role...</option>
                {roles.map((role) => (
                  <option key={role._id} value={role._id}>{role.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
              <select
                value={form.status}
                onChange={(e) => handleChange('status', e.target.value)}
                className={selectCls}
              >
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : user?._id ? 'Save Changes' : 'Create & Send Invitation'}
          </button>
        </div>
      </div>
    </div>
  );
}
